import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import emitter from 'tiny-emitter/instance';

export const useDashboardWidgetChannelStore = defineStore('dashboardWidgetChannel', () => {
  const dashboardChannels = ref(new Map());
  const dashboardWidgetChannels = ref(new Map());
  const lastUpdatedChannel = ref(null);
  const initializedStore = ref(false);

  function initializeDashboardWidgetChannels(dashboardWidgets) {
    initializedStore.value = false;
    clearDashboardWidgetChannels();
    clearDashboardChannels();

    dashboardWidgets.forEach(widget => {
      const channelsPerWidget = new Map();

      const connectedChannels = widget.connected_channels || {};
      Object.entries(connectedChannels).forEach(([k, v]) => {
        channelsPerWidget.set(k.toString(), v);
      });
      dashboardWidgetChannels.value.set(Number(widget.id), channelsPerWidget);
    });
    initializedStore.value = true;
  }

  function getDashboardWidgetChannels(widgetId) {
    return dashboardWidgetChannels.value.get(widgetId) || new Map();
  }

  async function setDashboardWidgetChannel(widgetId, param, channel, readOnly = false) {
    const widgetChannels = getDashboardWidgetChannels(widgetId);

    const isCircular = isCircularChannel(widgetChannels, param, channel);
    if (!isCircular) {
      widgetChannels.set(param, channel);

      await setDashboardWidgetChannels(widgetId, widgetChannels, readOnly);
    }
    return !isCircular;
  }

  async function updateDashboardWidget(widgetId, connectedChannels, readOnly = false) {
    const payload = { connected_channels: mapToJSON(connectedChannels) };

    if (readOnly) await updateWidgetOverride(payload, widgetId);
    else await updateWidget(payload, widgetId);
  }

  function updateDashboardWidgetChannels(widgetId, connectedChannels) {
    removeCircularChannels(connectedChannels);

    dashboardWidgetChannels.value.set(widgetId, connectedChannels);
  }

  function updateDashboardWidgetChannel(widgetId, param, channel) {
    const widgetChannels = getDashboardWidgetChannels(widgetId);

    const isCircular = isCircularChannel(widgetChannels, param, channel);
    if (!isCircular) {
      widgetChannels.set(param, channel);

      updateDashboardWidgetChannels(widgetId, widgetChannels);
    }
    return !isCircular;
  }

  async function setDashboardWidgetChannels(widgetId, connectedChannels, readOnly = false) {
    updateDashboardWidgetChannels(widgetId, connectedChannels);

    await updateDashboardWidget(widgetId, connectedChannels, readOnly);
  }

  // Assumes only updating parameters (ModalEditWidget)
  function removeCircularChannels(connectedChannels) {
    const paramChannels = [];
    connectedChannels.forEach((channel, param) => {
      if (!isNaN(param)) paramChannels.push([param, channel]);
    });

    paramChannels.forEach(([param, channel]) => {
      if (isCircularChannel(connectedChannels, param, channel)) {
        connectedChannels.delete(param);
      }
    });

    return connectedChannels;
  }

  // Assumes table columns are NaN and params are a number
  function isCircularChannel(connectedChannels, param, channel) {
    const isParamNaN = isNaN(param);

    let isCircular = false;
    connectedChannels.forEach((c, p) => {
      const isPNaN = isNaN(p);
      if (c == channel && p != param && isParamNaN != isPNaN) isCircular = true;
    });

    return isCircular;
  }

  function mapToJSON(mapObject) {
    const array = Array.from(mapObject);
    return Object.fromEntries(array);
  }

  async function updateWidgetOverride(payload, widgetId) {
    await axios.post('/widget_overrides', {
      widget_override: {
        dashboard_widget_id: widgetId,
        overrides: payload
      }
    });
  }

  async function updateWidget(payload, widgetId) {
    const widgetResponse = await axios.patch(`/dashboard_widgets/${widgetId}`, payload);
    emitter.emit('widget:update', { widget: widgetResponse.data });
  }

  function getDashboardChannel(channel) {
    return dashboardChannels.value.get(channel);
  }

  function setDashboardChannel(channel, value) {
    lastUpdatedChannel.value = channel;
    if (channel && getDashboardChannel(channel) != value) dashboardChannels.value.set(channel, value);
  }

  function clearDashboardChannels() {
    dashboardChannels.value.clear();
  }

  function clearDashboardWidgetChannels() {
    dashboardWidgetChannels.value.clear();
  }

  return {
    dashboardWidgetChannels,
    dashboardChannels,
    lastUpdatedChannel,
    initializedStore,
    initializeDashboardWidgetChannels,
    getDashboardWidgetChannels,
    setDashboardWidgetChannel,
    setDashboardWidgetChannels,
    updateDashboardWidgetChannel,
    updateDashboardWidgetChannels,
    getDashboardChannel,
    setDashboardChannel,
    clearDashboardChannels,
    clearDashboardWidgetChannels
  };
});
